import * as React from 'react';
import { Alert, Form, Input, Collapse, Button, Col, Dropdown, Menu, Modal, Result, Row, Space, Steps, Table, Tag, Tooltip, List, Typography } from 'antd';
import * as Icons from '@ant-design/icons';
import { AntForm, Details, SuspenseSpin, TagSpace } from '@core/ui';
import { useHistory } from 'react-router-dom';
import { useAuth } from '@modules/auth';
import { CouponTags } from '@modules/coupons/constants';
import { useAddCommercial, useDeclaration } from '../hooks';
import { DeclarationStatusTag } from '../components';
import { Formik } from 'formik';
import { TextField, TextAreaField } from '@core/form';
import TrendyolLogo from '../../../../assets/images/trendyol.svg';
import TemuLogo from '../../../../assets/images/temu.svg';
import ReactJson from 'react-json-view';
import { BoxTransfersPage } from '@modules/box-transfers/pages';
import { BoxTransfersRequestType } from '@modules/box-transfers';
import { RenderIf } from '@core/components';
import { CurrencySymbols, getCurrencySymbolByCountryId } from '@infra/utils';
import { getCurrencySymbol } from '@modules/transactions/utils/get-currency-symbol';
import { CountryTag } from '@modules/countries';
import { useNotification } from '@modules/notifications';

const tooltipStyle = {
  background: '#ffffff',
  color: '#262626',
  boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
  padding: 10,
  borderRadius: 6,
};

const tooltipContentStyle = { maxWidth: 300 };

const tooltipTitleStyle = { color: '#262626' };

const tooltipTextStyle = {
  fontSize: 12,
  color: '#595959',
  lineHeight: 1.6,
};

const iconStyle = {
  color: '#1890ff',
  cursor: 'pointer',
};

export const DeclarationDetails: React.FC<{ id: string }> = ({ id }) => {
  const { go } = useHistory();
  const { can } = useAuth();
  const notification = useNotification();

  const [isCustomsRawModalOpen, setIsCustomsRawModalOpen] = React.useState(false);

  const {
    returnDec,
    handover,
    canHandover,
    customsStatus,
    customStatusName,
    waybillIsDisabled,
    openEdit,
    openTimeline,
    openOrder,
    orderUrls,
    printWaybill,
    printProformaInvoice,
    cancelDispatch,
    remove,
    data,
    isLoading,
    error,
    removeFromFlight,
    parcelStates,
    trendyolStatus,
    changeTrendyolStatus,
    customsStatusModalOpen,
    openCustomsStatusModal,
    closeCustomsStatusModal,
    openContainerTransfersModal,
    closeContainerTransfersModal,
    isContainerTransfersModalVisible,
    customsStatesInfo,
    pinCodeEditing,
    onTogglePinCodeEditing,
    onPinCodeSubmit,
    form,
    customsStatusName,
    onPrintIndividualHandoverCheck,
    onPrintHandoverCheck,
    handleAccordionChange,
    isAccordionOpen,
    handleToggleWantedStatus,
    isTogglingWanted,
    wantedModalVisible,
    closeWantedModal,
    handleSubmitWanted,
    handleRemoveFromContainer,
  } = useDeclaration(id);

  const { initialValues, addCommercialVoen, closeModal, commercialModalOpen, openModal } = useAddCommercial(+id, data);

  React.useEffect(() => {
    if (data?.isWanted) {
      notification.warning({
        message: 'Diqqət!',
        description: 'Bu bağlama axtarış siyahısındadır. Bağlama axtarılır və xüsusi diqqət tələb edir.',
        duration: 8,
        placement: 'topRight',
      });
    }
  }, [data?.isWanted, notification]);

  const ordersNode = (
    <Dropdown
      disabled={!orderUrls.data?.length}
      overlay={
        <Menu>
          {orderUrls.data?.map((id, index) => (
            <Menu.Item onClick={() => openOrder(id)} key={id}>
              Sifariş #{index + 1}
            </Menu.Item>
          ))}
        </Menu>
      }
    >
      <Button type='primary' icon={<Icons.ShoppingCartOutlined />} ghost={true} block={true}>
        Sifarişlər
      </Button>
    </Dropdown>
  );

  const getAccordionHeader = React.useCallback(() => {
    if (customsStatus.isLoading) {
      return (
        <Tooltip title='Yüklənir...'>
          <Space size={6} align='center'>
            <Icons.LoadingOutlined color='#1890ff' />
            <Typography.Text strong>DGK Status</Typography.Text>
          </Space>
        </Tooltip>
      );
    }
    if (customsStatus.isError) {
      return (
        <Tooltip title='Yenidən cəhd edin'>
          <Space size={6} align='center'>
            <Icons.ReloadOutlined color='#ff4d4f' />
            <Typography.Text strong>DGK Status</Typography.Text>
            <Typography.Text type='secondary'> (Yenidən cəhd edin)</Typography.Text>
          </Space>
        </Tooltip>
      );
    }
    return (
      <Tooltip title='DGK Statusu'>
        <Space size={6} align='center'>
          <Icons.FileTextOutlined color='#52c41a' />
          <Typography.Text strong>DGK Status</Typography.Text>
          <Typography.Text type='secondary'>{!customsStatus.isSuccess ? '(Məlumatı görmək üçün klikləyin)' : ''}</Typography.Text>
        </Space>
      </Tooltip>
    );
  }, [customsStatus.isLoading, customsStatus.isError, customsStatus.isSuccess]);

  if (isLoading) {
    return (
      <Details.Wrapper>
        <SuspenseSpin />
      </Details.Wrapper>
    );
  }

  if (error) {
    return (
      <Details.Wrapper>
        <Result status='404' title='Xəta baş verdi' subTitle={error} />
      </Details.Wrapper>
    );
  }

  if (!data) {
    return null;
  }

  const title = (
    <Space size={8}>
      <Icons.LeftCircleOutlined onClick={() => go(-1)} />
      <span>#{data.trackCode}</span>
    </Space>
  );

  const tags = (
    <TagSpace size={8}>
      <CountryTag countryId={data.countryId} />
      <DeclarationStatusTag key='status-tag' id={data.status.id} name={data.status.name} />
      {data.isCommercial ? (
        <Tag color='blue' key='commercial-tag'>
          Kommersial bağlama
        </Tag>
      ) : (
        <Tag key='commercial-tag'>Vətəndaş bağlaması</Tag>
      )}
      {data.returned && (
        <Tag color='#e77b8a' key='returned'>
          İadə edilmişdir
        </Tag>
      )}

      {data.trendyol === 1 && <img style={{ width: 80, height: 30 }} src={TrendyolLogo} alt='Trendyol' />}
      {data.trendyol === 2 && <img style={{ width: 60, height: 40 }} src={TemuLogo} alt='Temu' />}
    </TagSpace>
  );

  const extra = [
    <Tooltip key='state-timeline-button' title='Status xəritəsi'>
      <Button type='link' onClick={openTimeline} icon={<Icons.FieldTimeOutlined />} />
    </Tooltip>,
    <Tooltip key='edit-button' title='Düzəliş et'>
      <Button type='link' onClick={openEdit} icon={<Icons.EditOutlined />} />
    </Tooltip>,
    <Tooltip key='delete-button' title='Sil'>
      <Button type='link' disabled={!can('declaration_cancel')} onClick={remove} danger={true} icon={<Icons.DeleteOutlined />} />
    </Tooltip>,
  ];

  return (
    <Details.Wrapper>
      <Details.Row>
        <Details.Col xs={24}>
          <Details.PageHeader title={title} subTitle={data.user.name} tags={tags} extra={extra} />
          {!!data.editable && <Alert message='Bu bağlama məlumatları natamam doldurulduğundan gömrüyə göndərilməyib.' type='warning' showIcon />}
        </Details.Col>
        <Details.Col xs={24}>
          <Details.Actions>
            <Details.ActionCol>
              <Button type='primary' disabled={!canHandover} onClick={handover} icon={<Icons.CheckCircleOutlined />} ghost={true} block={true}>
                Təhvil ver
              </Button>
              <Button type='primary' onClick={returnDec} icon={<Icons.RollbackOutlined />} ghost={true} block={true}>
                İadə et
              </Button>
              {can('canceldepesh') && (
                <Button type='primary' onClick={cancelDispatch} icon={<Icons.IssuesCloseOutlined />} disabled={data.status.id !== 8} ghost={true} block={true}>
                  Depeşi ləğv et
                </Button>
              )}
              <Dropdown
                overlay={
                  <Menu>
                    <Menu.Item onClick={printWaybill} disabled={waybillIsDisabled}>
                      Yol vərəqi
                    </Menu.Item>
                    <Menu.Item onClick={printProformaInvoice}>Proforma invoice</Menu.Item>
                    <Menu.Item onClick={onPrintHandoverCheck} disabled={!data?.handoverTaskId}>
                      Təhvil sənədi
                    </Menu.Item>
                    <Menu.Item onClick={onPrintIndividualHandoverCheck}>Təhvil sənədi(fərdi)</Menu.Item>
                  </Menu>
                }
              >
                <Button type='primary' icon={<Icons.PrinterOutlined />} ghost={true} block={true}>
                  Çap et
                </Button>
              </Dropdown>
              {data.trendyol === 1 && (
                <Dropdown
                  overlay={
                    <Menu>
                      {trendyolStatus.data?.map((elem) => (
                        <Menu.Item key={elem.id} onClick={() => changeTrendyolStatus(elem.id, elem.name)}>
                          {elem.name}
                        </Menu.Item>
                      ))}
                    </Menu>
                  }
                >
                  <Button type='primary' icon={<Icons.EditFilled />} ghost={true} block={true}>
                    Trendyol Statusunu dəyiş
                  </Button>
                </Dropdown>
              )}

              {data.isCommercial && (
                <Button type='primary' onClick={openModal} icon={<Icons.FileMarkdownOutlined />} ghost={true} block={true}>
                  Kommersial bəyan
                </Button>
              )}
            </Details.ActionCol>
            <Details.ActionCol>{ordersNode}</Details.ActionCol>
          </Details.Actions>
        </Details.Col>
        <Details.Col xs={24} lg={12}>
          <Details.Row>
            <Details.Col xs={24}>
              <Details.Card title='Ümumi məlumat'>
                <Details.Descriptions bordered={true} column={1} size='small'>
                  <Details.Descriptions.Item label='Müştəri kodu'>{data.user.id}</Details.Descriptions.Item>
                  <Details.Descriptions.Item label='İzləmə kodu'>#{data.trackCode}</Details.Descriptions.Item>
                  <Details.Descriptions.Item label='Qlobal izləmə kodu'>
                    {data.globalTrackCode} {data.trendyol === 1 ? `(${data.trendyolLogs?.barcode})` : ''}
                  </Details.Descriptions.Item>
                  {!!data.partnerName && <Details.Descriptions.Item label='Partnyor'>{data.partnerName}</Details.Descriptions.Item>}
                  <Details.Descriptions.Item label='Gömrük bəyanı'>
                    <Space size={6} align='center'>
                      <span>{data.customs === 0 ? 'edilməyib' : 'edilib'}</span>
                      <RenderIf condition={!!data?.customsData?.raw_data}>
                        <Tooltip title='DGK cavabını JSON formatda göstər'>
                          <Icons.FileSearchOutlined style={{ color: '#1890ff', cursor: 'pointer', fontSize: 16 }} onClick={() => setIsCustomsRawModalOpen(true)} />
                        </Tooltip>
                      </RenderIf>
                    </Space>
                    <RenderIf condition={!!data?.customsData?.created_at}>
                      <Typography.Text type='secondary' style={{ fontSize: '12px', display: 'block' }}>
                        {data?.customsData?.created_at}
                      </Typography.Text>
                    </RenderIf>
                  </Details.Descriptions.Item>
                  <Details.Descriptions.Item label='İadə'>{data.returned ? 'Bəli' : 'Xeyr'}</Details.Descriptions.Item>
                  <Details.Descriptions.Item label='Axtarılır'>
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                      <Space size={8}>
                        <Typography.Text>{data.isWanted ? 'Bəli' : 'Xeyr'}</Typography.Text>
                        <RenderIf condition={data.isWanted}>
                          <Tooltip
                            mouseEnterDelay={0.2}
                            overlayInnerStyle={tooltipStyle}
                            title={
                              <div style={tooltipContentStyle}>
                                <Typography.Text strong style={tooltipTitleStyle}>
                                  Axtarış səbəbi:
                                </Typography.Text>
                                <br />
                                <Typography.Text style={tooltipTextStyle}>{data.wantedDescription || 'Qeyd edilməyib'}</Typography.Text>
                              </div>
                            }
                          >
                            <Icons.InfoCircleOutlined style={iconStyle} />
                          </Tooltip>
                        </RenderIf>
                      </Space>
                      {can('wanted_parcel') && (
                        <Button type='primary' size='small' loading={isTogglingWanted} onClick={handleToggleWantedStatus} ghost>
                          {data.isWanted ? 'Axtarışdan çıxar' : 'Axtarışa ver'}
                        </Button>
                      )}
                    </Space>
                  </Details.Descriptions.Item>
                  <Details.Descriptions.Item label='Məhsul tipi'>{data.productType.name}</Details.Descriptions.Item>
                  <Details.Descriptions.Item label='Mağaza'>{data.shop}</Details.Descriptions.Item>
                  <Details.Descriptions.Item label='Tərkibi'>{data.type === 'liquid' ? 'Maye' : 'Digər'}</Details.Descriptions.Item>
                  <Details.Descriptions.Item label='Miqdarı'>{data.quantity}</Details.Descriptions.Item>
                  {data.voen && <Details.Descriptions.Item label='VÖEN'>{data.voen}</Details.Descriptions.Item>}
                  <Details.Descriptions.Item label='Sənəd'>
                    {data.file ? (
                      <a href={data.file} target='_blank' rel='noreferrer noopener'>
                        Sənədə bax
                      </a>
                    ) : (
                      'Mövcud deyil'
                    )}
                  </Details.Descriptions.Item>
                  <Details.Descriptions.Item label='Yaradılma tarixi'>{data.createdAt}</Details.Descriptions.Item>
                  {data.causerId && <Details.Descriptions.Item label='Düzəliş edən'>{data.causerId}</Details.Descriptions.Item>}
                </Details.Descriptions>
              </Details.Card>
            </Details.Col>
            <Details.Col xs={24}>
              <Details.Card title='Anbar məlumatları'>
                <Details.Descriptions bordered={true} column={1} size='small'>
                  <Details.Descriptions.Item label='Səbət'>{data.basket?.name || 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                  <Details.Descriptions.Item label='Koli'>{data.parcel?.id || 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                  <Details.Descriptions.Item label='Şkaf (Yerli)'>{data.wardrobeNumber || 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                  <Details.Descriptions.Item label='Yeşik'>
                    <Space size={12} align='center'>
                      <Button type='text' disabled={!data.lastBox?.id} onClick={openContainerTransfersModal} icon={<Icons.HistoryOutlined />} />
                      {!!data.lastBox ? (
                        <Tooltip title={data.status.id === 10 ? 'Köhnə yeşik' : undefined}>
                          <Tag color={data.status.id === 10 ? 'success' : undefined}>{data.lastBox?.name}</Tag>
                        </Tooltip>
                      ) : (
                        <Typography.Text>Qeyd olunmayıb</Typography.Text>
                      )}
                      {data.box && can('remove_parcel_from_container') && (
                        <Button type='primary' onClick={handleRemoveFromContainer} icon={<Icons.DeleteOutlined />} size='small' ghost danger>
                          Yeşikdən çıxar
                        </Button>
                      )}
                    </Space>
                  </Details.Descriptions.Item>
                </Details.Descriptions>
              </Details.Card>
            </Details.Col>
            {!!Object.keys(data?.trendyolLogs || {}).length && (
              <Details.Col xs={24}>
                <Details.Card title='United Məhsul'>
                  <Details.Descriptions bordered={true} column={1} size='small'>
                    <Details.Descriptions.Item label='Məhsul adı'>{data.trendyolLogs?.name || 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                    <Details.Descriptions.Item label='Kateqoriya'>{data.trendyolLogs?.category || 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                    <Details.Descriptions.Item label='Maye'>{data.trendyolLogs?.isLiquid ? 'Bəli' : 'Xeyr'}</Details.Descriptions.Item>
                    <Details.Descriptions.Item label='Qapıda təhvil'>{data.trendyolLogs?.isDoor ? 'Bəli' : 'Xeyr'}</Details.Descriptions.Item>
                    <Details.Descriptions.Item label='Növ'>{data.trendyolLogs?.type || 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                    <Details.Descriptions.Item label='Micro'>{data.trendyolLogs?.isMicro ? 'Bəli' : 'Xeyr'}</Details.Descriptions.Item>
                    <Details.Descriptions.Item label='İnvoys qiyməti'>
                      {data.trendyolLogs?.invoice.invoicePrice}
                      <a target='_blank' href={data.trendyolLogs?.invoice.invoiceUrl} rel='noreferrer noopener'>
                        {' '}
                        PDF
                      </a>
                    </Details.Descriptions.Item>
                    <Details.Descriptions.Item label='SKU'>{data.trendyolLogs?.products.sku || 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                    <Details.Descriptions.Item label='Provayder trak kod'>{data.trendyolLogs?.trendyolDeliveryNumber || 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                    <Details.Descriptions.Item label='Vahidin qiyməti'>{data.trendyolLogs?.unitPrice || 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                    <Details.Descriptions.Item label='Sayı'>{data.trendyolLogs?.quantity || 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                    <Details.Descriptions.Item label='State'>{data.trendyolLogs?.state || 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                    <Details.Descriptions.Item label='Parcel id'>{data.trendyolLogs?.parcelId || 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                    <Details.Descriptions.Item label='UIDs'>{data.trendyolLogs?.uid || 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                    <Details.Descriptions.Item label='Gömrük vəziyyəti'>{customsStatusName || 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                  </Details.Descriptions>
                </Details.Card>
              </Details.Col>
            )}
          </Details.Row>
        </Details.Col>
        <Details.Col xs={24} lg={12}>
          <Details.Row>
            <Details.Col xs={24}>
              <Details.Card title='Qiymətlər'>
                <Details.Descriptions bordered={true} column={1} size='small'>
                  <Details.Descriptions.Item label='Məhsulun qiyməti'>
                    {data.price ? `${data.price.toFixed(2)} ${data.currency ? getCurrencySymbol(data.currency) : getCurrencySymbolByCountryId(data.countryId)}` : 'Qeyd olunmayıb'}
                  </Details.Descriptions.Item>
                  <Details.Descriptions.Item label='Çatdırılma qiyməti'>{data.deliveryPrice ? `${data.deliveryPrice.toFixed(2)} ${CurrencySymbols.USD}` : 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                  <Details.Descriptions.Item label='Ödənilmə statusu'>{data.paid ? 'Ödənilib' : 'Ödənilməyib'}</Details.Descriptions.Item>
                  <Details.Descriptions.Item label='Kupon'>{data.couponId ? CouponTags[data.couponId] : '-'}</Details.Descriptions.Item>
                </Details.Descriptions>
              </Details.Card>
            </Details.Col>
            <Details.Col xs={24}>
              <Details.Card title='Ölçülər'>
                <Details.Descriptions bordered={true} column={1} size='small'>
                  <Details.Descriptions.Item label='Çəki'>{data.weight ? `${data.weight.toFixed(2)} kq` : 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                  <Details.Descriptions.Item label='Volumn'>{data.trendyolLogs?.volume || 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                  <Details.Descriptions.Item label='En'>{data.depth ? `${data.depth.toFixed(2)} sm` : 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                  <Details.Descriptions.Item label='Uzunluq'>{data.width ? `${data.width.toFixed(2)} sm` : 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                  <Details.Descriptions.Item label='Hündürlük'>{data.height ? `${data.height.toFixed(2)} sm` : 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                </Details.Descriptions>
              </Details.Card>
            </Details.Col>

            <Details.Col xs={24}>
              <Details.Card title='Yerləşmə'>
                <Details.Descriptions bordered={true} column={1} size='small'>
                  {data.branch && <Details.Descriptions.Item label='Filial'>{data.branch.name || 'Qeyd olunmayıb'}</Details.Descriptions.Item>}
                  <Details.Descriptions.Item label='Uçuş'>
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                      <Row>
                        <Col span={24}>
                          <Typography.Text>{data.flight?.name || 'Qeyd olunmayıb'}</Typography.Text>
                        </Col>
                        {data.bbs?.user && (
                          <Col span={24}>
                            <Typography.Text style={{ fontStyle: 'italic', fontSize: '12px' }}>
                              {data.bbs?.user} tərəfindən {data.bbs?.date}
                            </Typography.Text>
                          </Col>
                        )}
                      </Row>
                      {can('take_of_from_flight') && (
                        <Button type='primary' onClick={removeFromFlight} icon={<Icons.DeleteOutlined />} size='small' ghost disabled={!data.flight?.name}>
                          Uçuşdan çıxar
                        </Button>
                      )}
                    </Space>
                  </Details.Descriptions.Item>
                  <Details.Descriptions.Item label='Filial göndərişi'>
                    <Row>
                      <Col span={24}>
                        <Typography.Text>{data.parcelSorting?.id ? `${data.parcelSorting?.id} (${data.parcelSorting?.state_name ?? ''})` : 'Çeşidlənməyib'}</Typography.Text>
                      </Col>
                      <Col span={24}>
                        <Typography.Text style={{ fontStyle: 'italic', fontSize: '12px' }}>{data.parcelSorting?.created_at}</Typography.Text>
                      </Col>
                    </Row>
                  </Details.Descriptions.Item>
                </Details.Descriptions>
              </Details.Card>
            </Details.Col>
            {!!Object.keys(data?.trendyolLogs || {}).length && (
              <Details.Col xs={24}>
                <Details.Card title='United Müştəri'>
                  <Details.Descriptions bordered={true} column={1} size='small'>
                    <Details.Descriptions.Item label='Tam adı'>{data.trendyolLogs?.fullName || 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                    <Details.Descriptions.Item label='FIN Kod'>
                      {!pinCodeEditing ? (
                        <Space align='center' size={6}>
                          <Typography.Text>{data.trendyolLogs?.pinCode || 'Qeyd olunmayıb'}</Typography.Text>
                          {can('parcel_pin_change') && <Button type='text' icon={<Icons.EditOutlined />} onClick={onTogglePinCodeEditing} />}
                        </Space>
                      ) : (
                        <>
                          <Form form={form} onFinish={(values) => onPinCodeSubmit(values, data.trendyol === 2)} layout='inline' initialValues={{ pincode: data.trendyolLogs?.pinCode }}>
                            <Form.Item name='pincode'>
                              <Input />
                            </Form.Item>
                          </Form>
                          <Typography.Text style={{ fontSize: '12px' }} type='secondary'>
                            FİN kod düzəlişi bir neçə dəqiqə ərzində icra edilir.
                          </Typography.Text>
                        </>
                      )}
                    </Details.Descriptions.Item>

                    <Details.Descriptions.Item label='Müştəri qeydi'>{data.trendyolLogs?.comment || 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                    <Details.Descriptions.Item label='Telefon nömrəsi'>{data.trendyolLogs?.phoneNumber || 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                    <Details.Descriptions.Item label='Yaradılma tarixi'>{data.trendyolLogs?.createdAt || 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                    <Details.Descriptions.Item label='Email'>{data.trendyolLogs?.emailAddress || 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                    <Details.Descriptions.Item label='ZIP Kod'>{data.trendyolLogs?.zipCode || 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                    <Details.Descriptions.Item label='Filial kodu'>{data.trendyolLogs?.warehouseId || 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                    <Details.Descriptions.Item label='Xarici karqo'>{data.trendyolLogs?.domesticCargoCompany || 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                    <Details.Descriptions.Item label='Şəhər'>{data.trendyolLogs?.city || 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                    <Details.Descriptions.Item label='SMS sayı'>{data.trendyolLogs?.smsCount || 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                    <Details.Descriptions.Item label='SMS tarix'>{data.trendyolLogs?.smsDate || 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                    <Details.Descriptions.Item label='Müştəri ünvanı'>{data.trendyolLogs?.shippingAddress || 'Qeyd olunmayıb'}</Details.Descriptions.Item>
                  </Details.Descriptions>
                </Details.Card>
              </Details.Col>
            )}
            {(data.trendyol === 1 || data.trendyol === 2) && (
              <Details.Col xs={24}>
                <Table size='small' rowKey='_id' pagination={false} dataSource={parcelStates} title={() => <h4>United Status dəyişiklikləri </h4>} bordered={true}>
                  <Table.Column title='Id' key='_id' dataIndex='_id' width={100} />
                  <Table.Column title='Author' key='author' dataIndex='author' />
                  <Table.Column title='State' key='state' dataIndex='state' />
                  <Table.Column title='Comment' key='comment' dataIndex='comment' />
                  <Table.Column title='Created At' key='createdAt' dataIndex='createdAt' />
                </Table>
              </Details.Col>
            )}
          </Details.Row>
        </Details.Col>

        <Details.Col xs={24}>
          <Details.Card title='Açıqlama'>{data.description || 'Qeyd olunmayıb'}</Details.Card>
        </Details.Col>
        <Details.Col xs={24}>
          <Collapse bordered={false} activeKey={isAccordionOpen ? ['customs-status'] : []} onChange={handleAccordionChange} collapsible={customsStatus.isLoading ? 'disabled' : undefined}>
            <Collapse.Panel header={getAccordionHeader()} key='customs-status'>
              <RenderIf condition={!customsStatus.isLoading && customsStatus.isSuccess}>
                <Details.Row style={{ backgroundColor: '#fff', paddingTop: '1rem' }}>
                  <Details.Col xs={24} style={{ display: 'flex', marginBottom: 0, justifyContent: 'center', gap: '0.5rem' }}>
                    <Details.SectionTitle>DGK statusu</Details.SectionTitle>
                    <Button onClick={openCustomsStatusModal} type='text' icon={<Icons.FileSearchOutlined />} />
                  </Details.Col>
                  <Details.Col xs={24} style={{ marginTop: 0 }}>
                    <Details.SectionCustomStatus>{customStatusName}</Details.SectionCustomStatus>
                  </Details.Col>
                  <Details.Col xs={24}>
                    <Details.StepsWrapper>
                      <Steps current={typeof customsStatus.data?.customsStatus === 'number' ? customsStatus.data.customsStatus : -1} responsive={true} progressDot={true}>
                        <Steps.Step title='Əlavə edilib' description='Karqo şirkəti bağlamanı əlavə edib' />
                        <Steps.Step title='Bəyan edilib' description='Müştəri bağlamanı bəyan edib' />
                        <Steps.Step title='Kolilənib' description='Bağlamalar kolilərə yığılıb' />
                        <Steps.Step title='Depesh göndərilib' description='Depesh sorğusu göndərilib' />
                      </Steps>
                    </Details.StepsWrapper>
                  </Details.Col>
                </Details.Row>
              </RenderIf>
            </Collapse.Panel>
          </Collapse>
        </Details.Col>
      </Details.Row>
      <Modal width={992} title='Yeşik transferləri' visible={isContainerTransfersModalVisible} onCancel={closeContainerTransfersModal} footer={null}>
        <BoxTransfersPage requestId={data.id} requestType={BoxTransfersRequestType.DECLARATION} />
      </Modal>
      <Modal width={800} title='DGK Statusu' visible={customsStatusModalOpen} onCancel={closeCustomsStatusModal} footer={null}>
        <ReactJson src={customsStatus.data?.json || {}} />
        <List
          size='small'
          header={<Typography.Text>Birbaşa gömrükdən gələn cavabdır və status parametrinin izahı aşağıdakı kimidir.</Typography.Text>}
          dataSource={customsStatesInfo}
          renderItem={(item) => (
            <List.Item>
              <Typography.Text type='secondary'>
                {item.id} - {item.label}
              </Typography.Text>
            </List.Item>
          )}
        />
        <p></p>
      </Modal>
      <Modal width={800} title='DGK cavabı' visible={isCustomsRawModalOpen} onCancel={() => setIsCustomsRawModalOpen(false)} footer={null} bodyStyle={{ backgroundColor: '#ffffff' }}>
        <div style={{ backgroundColor: '#ffffff', color: '#000' }}>
          <ReactJson src={data.customsData?.raw_data || {}} />
        </div>
      </Modal>
      <Formik
        initialValues={{ descr: '' }}
        enableReinitialize
        onSubmit={async (values) => {
          await handleSubmitWanted(values.descr || null);
        }}
      >
        {({ handleSubmit, resetForm }) => {
          return (
            <Modal
              title='Axtarışa ver'
              visible={wantedModalVisible}
              onOk={() => handleSubmit()}
              onCancel={() => {
                resetForm();
                closeWantedModal();
              }}
              okText='Təsdiq et'
              cancelText='Ləğv et'
              confirmLoading={isTogglingWanted}
              width={500}
            >
              <AntForm layout='vertical' component='div' size='large'>
                <TextAreaField name='descr' item={{ label: 'Axtarış səbəbi' }} input={{ placeholder: 'Axtarış səbəbini daxil edin...', rows: 4, maxLength: 250, showCount: true }} />
                <Typography.Text type='secondary' style={{ fontSize: '12px' }}>
                  Maksimum 250 simvol
                </Typography.Text>
              </AntForm>
            </Modal>
          );
        }}
      </Formik>
      <Formik initialValues={initialValues} enableReinitialize onSubmit={() => {}}>
        {({ values, setErrors, handleReset }) => {
          return (
            <Modal visible={commercialModalOpen} onCancel={closeModal} onOk={() => addCommercialVoen(values, setErrors, handleReset)} title='Kommersial bəyan'>
              <AntForm layout='vertical' component='div' size='large'>
                <Row gutter={[24, 0]}>
                  <Col lg={24} md={24}>
                    <TextField name='declarationId' item={{ label: 'Bağlama' }} input={{ placeholder: 'Bağlama kodunu daxil edin...', disabled: true }} />
                  </Col>
                  <Col lg={24} md={24}>
                    <TextField name='awb' item={{ label: 'Avia Qaimə' }} input={{ placeholder: 'Avia Qaimə kodunu daxil edin...' }} />
                  </Col>
                  <Col lg={24} md={24}>
                    <TextField name='voen' item={{ label: 'VÖEN' }} input={{ placeholder: 'VÖEN daxil edin...' }} />
                  </Col>
                </Row>
              </AntForm>
            </Modal>
          );
        }}
      </Formik>
    </Details.Wrapper>
  );
};
